/**
 * RitualFriendCircle Component
 * Clickable card promoting the "Build Your Ritual Circle" feature with navigation to connect page
 */

import { X } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib"
import { getAssetUrl } from "@/utils/assets"

interface RitualFriendCircleProps {
  className?: string
  onDismiss?: () => void
  storageKey?: string
}

export function RitualFriendCircle({
  className,
  onDismiss,
  storageKey = "ritual-friend-circle-dismissed",
}: RitualFriendCircleProps) {
  const navigate = useNavigate()
  const [isDismissed, setIsDismissed] = useState(false)

  useEffect(() => {
    const dismissed = localStorage.getItem(storageKey)
    if (dismissed === "true") {
      setIsDismissed(true)
    }
  }, [storageKey])

  const handleClick = () => {
    navigate("/settings?section=invite-friends")
  }

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsDismissed(true)
    localStorage.setItem(storageKey, "true")
    onDismiss?.()
  }

  if (isDismissed) {
    return null
  }

  return (
    <Button
      variant="default"
      onClick={handleClick}
      className={cn(
        "relative w-full h-full flex gap-5 justify-start bg-[#C9A8761A] border-[#C9A8761A] text-white rounded-xl p-5 transition-colors text-left cursor-pointer",
        className,
      )}
    >
      <div className="shrink-0">
        <div className="w-12 h-12 bg-[#C9A87626] rounded-full flex items-center justify-center">
          <img src={getAssetUrl("people_add.svg")} alt="Add people" className="w-6.25 h-6.25" />
        </div>
      </div>
      <div className="flex flex-col gap-1.25 min-w-0">
        <h3 className="font-semibold text-[16px] leading-5 text-[#F2F0EB]">
          Build Your Ritual Circle
        </h3>
        <p className="text-[13px] text-wrap leading-5 text-[#F2F0EBE6]">
          <a
            href="/settings?section=invite-friends"
            className="text-[#F2F0EB] underline cursor-pointer"
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
              navigate("/settings?section=invite-friends")
            }}
          >
            Find friends
          </a>{" "}
          & bring your friends in and build shared intentions, streaks, and moments of presence.
        </p>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute right-5 top-5 shrink-0 p-1 opacity-50 hover:opacity-80 transition-opacity cursor-pointer"
        aria-label="Dismiss banner"
      >
        <X className="w-[15px] h-[15px] text-[#F2F0EB]" />
      </button>
    </Button>
  )
}
